import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DateNullDefault, DropdownBookingStatus, DropdownBookingType, DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete, ViewDateFormat } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { GetBookingStatus, GetBookingType, GetStatus } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import moment from "moment";
import { MainBodyCard } from "components/Cards/MainBodyCard";
import Pagination from "components/Input/PaginationButton";
import { BookingService } from "services/BookingService";
import { useModal } from "utils/context/ModalContext";
import DataTable from "components/DataTable";
import { GridEditButton } from "components/Button/GridEditButton";
import { GridDeleteButton } from "components/Button/GridDeleteButton";
import { FilterCard } from "components/Cards/FilterCard";
import PrintButton from "components/Button/PrintButton";

export default function Booking(){
    let navigate = useNavigate()

    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()

    const { fetchBookingList, fetchCompleteBooking, fetchDeleteBooking } = BookingService()

    // FILTER
    const [tenantName, setTenantName] = useState(EmptyString)
    const [roomNumber, setRoomNumber] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [bookingStatus, setBookingStatus] = useState(DropdownBookingStatus[0].value)
    const [bookingType, setBookingType] = useState('')

    // DATA
    const [bookingList, setBookingList] = useState([])

    // Grid
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "tenantName",
            header: "Nama Tenant"
        },
        {
            key: "bookingDate",
            header: "Booking Date",
            render: (row) => moment(row.bookingDate).format('DD MMM YYYY')
        },
        {
            key: "checkInDate",
            header: "Check In Date",
            render: (row) => moment(row.checkInDate).format('DD MMM YYYY')
        },
        {
            key: "checkOutDate",
            header: "Check Out Date",
            render: (row) => moment(row.checkOutDate).format('DD MMM YYYY') === '01 Jan 0001' ? '-' : moment(row.checkOutDate).format('DD MMM YYYY')
        },
        {
            key: "roomNumber",
            header: "Room Number",
        },
        {
            key: "bookingStatus",
            header: "Booking Status",
            render: (row) => GetBookingStatus(String(row.bookingStatus))
        },
        {
            key: "bookingType",
            header: "Booking Type",
            render: (row) => GetBookingType(String(row.bookingType))
        },
        {
            key: "",
            header: "Lama Sewa",
            render: (row) => {
                // 1. Determine check-out date (use current date if '01 Jan 0001' or invalid)
                const checkOut = moment(row.checkOutDate).format('DD MMM YYYY') === '01 Jan 0001' 
                    ? moment() 
                    : moment(row.checkOutDate);

                const checkIn = moment(row.checkInDate);

                // 2. Get total days difference
                const totalDays = checkOut.diff(checkIn, 'days');

                // 3. Format into X month(s) Y day(s) if >= 30 days
                let rentDuration;

                if (totalDays >= 30) {
                const months = Math.floor(totalDays / 30);
                const remainingDays = totalDays % 30;

                const monthText = `${months} ${months === 1 ? 'month' : 'months'}`;
                const dayText = remainingDays > 0 ? ` ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}` : '';

                rentDuration = `${monthText}${dayText}`;
                } else {
                rentDuration = `${totalDays} ${totalDays === 1 ? 'day' : 'days'}`;
                }

                return (
                    <div>
                        <span>{rentDuration}</span>
                    </div>
                )
            }
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    {/* <GridViewButton onClick={() => viewBtnClick(row.customerId)} size="w-3" /> */}
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.bookingId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.bookingId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    const handlePageChange = async (newIndex) => {
        try {
            showLoading()

            const res = await fetchBookingList(
                tenantName, 
                roomNumber, 
                bookingStatus, 
                bookingType, 
                status, 
                PageSize, 
                newIndex
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setBookingList(res)
            setTotalPages(total)
            setPageIndex(newIndex)
        } finally {
            hideLoading()
        }
    }

    const addBtnClick = () => {
        navigate("/booking/form?bookingId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (bookingId) => {
        navigate("/booking/form?bookingId=" + bookingId + "&formType=Edit")
    }
    const deleteBtnClick = async (bookingId, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchDeleteBooking(bookingId, status)
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }
    const completeBtnClick = async (bookingId) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchCompleteBooking(bookingId)
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[tenantName, roomNumber, bookingStatus, bookingType, status])

    return(
        <MainBodyCard>
            <div >
                <span className="font-semibold text-2xl">Booking</span>
            </div>
            <hr />

            <div className="flex flex-col sm:flex-row gap-5 justify-between mt-5">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Tenant Name' width='w-full sm:w-64'>
                        <input className="input" value={tenantName} onChange={(e) => setTenantName(e.target.value.toUpperCase())} />
                    </FilterCard>
                    
                    <FilterCard title='Room' width='w-full sm:w-64'>
                        <input className="input" value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} />
                    </FilterCard>

                    <FilterCard title='Booking Status' width='w-full sm:w-36'>
                        <Dropdown className="w-full" options={DropdownBookingStatus} value={bookingStatus} onChange={(e) => setBookingStatus(e.value)} />
                    </FilterCard>

                    <FilterCard title='Booking Type' width='w-full sm:w-36'>
                        <Dropdown className="w-full" options={DropdownBookingType} value={bookingType} onChange={(e) => setBookingType(e.value)} placeholder={'All'} />
                    </FilterCard>

                    <FilterCard title='Status' width='w-full sm:w-36'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => setStatus(e.value)} placeholder={"All"} />
                    </FilterCard>
                </div>
            </div>

            <div className="mt-5 w-full flex gap-3 justify-end">
                <PrintButton />
                <AddButton onClick={addBtnClick} title='Tambah Booking' />
            </div>
           
            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={bookingList?.data?.listData ?? []}
                />
            </div>

            <div className="mt-5 sm:hidden flex flex-col gap-2">
                {bookingList?.data?.listData?.map((data, i) => {
                    return(
                        <div className="border rounded-md hover:bg-slate-100 bg-white py-2 px-2 flex justify-between" key={ i }>
                            <div className="text-sm">
                                <div>
                                    <span>{ data.tenantName.toUpperCase() }</span>
                                </div>                                
                                <div>
                                    <span className="">{ moment(data.checkInDate).format(ViewDateFormat) + " - " + (data.checkOutDate === DateNullDefault ? "" : moment(data.checkOutDate).format(ViewDateFormat)) }</span>
                                </div>
                                <div className="mt-1 flex gap-2">
                                    <button className="bg-black px-3 py-1 rounded-md flex gap-2" onClick={() => editBtnClick(data.bookingId)}>
                                        <EditIcon className="w-4 fill-white" />
                                        <span className="text-white">Edit</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.bookingId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                        <span className="text-white">Delete</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.bookingId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                        <span className="text-white">Undelete</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col text-right text-sm">
                                <div>
                                    <span>{ data.roomNumber }</span>
                                </div>
                                <div>
                                    <span>{ GetBookingStatus(String(data.bookingStatus)) }</span>
                                </div>
                                <div className="flex h-full items-center justify-end">
                                    <span>{  }</span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}